import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { StarIcon } from '@heroicons/react/24/solid';
import { ClockIcon, MapPinIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const FeaturedTours = () => {
  const [tours, setTours] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  
  // Fetch featured tours from API
  useEffect(() => {
    const fetchFeaturedTours = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/api/v1/tours/featured`);
        console.log('Featured tours response:', response.data);
        
        if (response.data.success) {
          // Handle both array and nested object formats for backward compatibility
          if (Array.isArray(response.data.data)) {
            setTours(response.data.data);
          } else if (response.data.data && response.data.data.data) {
            setTours(response.data.data.data);
          } else {
            setTours(response.data.data || []);
          }
          setError(null);
        } else {
          console.error('API returned error:', response.data.message);
          setError(response.data.message || 'Failed to load featured tours');
        }
      } catch (err) {
        console.error('Error fetching featured tours:', err);
        setError('Failed to load featured tours. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedTours();
  }, [apiUrl]);

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="tour-card animate-pulse rounded-xl overflow-hidden shadow-lg">
            <div className="h-56 bg-gray-300 rounded-t-xl"></div>
            <div className="p-5">
              <div className="h-6 bg-gray-300 rounded-full w-3/4 mb-3"></div>
              <div className="flex items-center mb-3">
                <div className="h-4 w-4 bg-gray-300 rounded-full mr-2"></div>
                <div className="h-4 bg-gray-300 rounded-full w-1/4"></div>
              </div>
              <div className="flex items-center mb-3">
                <div className="h-4 w-4 bg-gray-300 rounded-full mr-2"></div>
                <div className="h-4 bg-gray-300 rounded-full w-2/3"></div>
              </div>
              <div className="flex items-center mb-4">
                <div className="h-4 w-4 bg-gray-300 rounded-full mr-2"></div>
                <div className="h-4 bg-gray-300 rounded-full w-1/3"></div>
              </div>
              <div className="flex justify-between items-center pt-2">
                <div className="h-6 bg-gray-300 rounded-full w-1/4"></div>
                <div className="h-8 bg-gray-300 rounded-full w-1/4"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  // No tours available
  if (!tours || tours.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No featured tours available at the moment. Please check back later.</p>
      </div>
    );
  }

  // Format price with commas
  const formatPrice = (price) => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleImageError = (tourId) => {
    setImageErrors(prev => ({
      ...prev,
      [tourId]: true
    }));
  };

  // Fallback images for different tour types
  const getFallbackImage = (category) => {
    const fallbacks = {
      'Wildlife Safari': 'https://images.unsplash.com/photo-1504173010664-32509aeebb62?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Beach Vacation': 'https://images.unsplash.com/photo-1520454974749-611b7248ffdb?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Mountain Climbing': 'https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Adventure': 'https://images.unsplash.com/photo-1528733918455-5a59687cedf0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Cultural Experience': 'https://images.unsplash.com/photo-1489493585363-d69421e0edd3?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'City Tour': 'https://images.unsplash.com/photo-1611348524140-53c9a25ad10a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'default': 'https://images.unsplash.com/photo-1528733918455-5a59687cedf0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80'
    };
    
    if (!category) return fallbacks.default;
    
    // If we have categories as an array, use the first one
    const categoryName = Array.isArray(category) && category.length > 0 
      ? category[0].name 
      : (typeof category === 'string' ? category : 'default');
      
    return fallbacks[categoryName] || fallbacks.default;
  };

  // Get image URL
  const getImageUrl = (tour) => {
    if (imageErrors[tour.id]) return getFallbackImage(tour.categories);
    
    // If image is a full URL
    if (tour.image && (tour.image.startsWith('http://') || tour.image.startsWith('https://'))) {
      return tour.image;
    }
    
    // If image is a relative path
    if (tour.image) {
      return `${apiUrl}/storage/${tour.image}`;
    }
    
    // Try featured_image if image is not available
    if (tour.featured_image) {
      if (tour.featured_image.startsWith('http://') || tour.featured_image.startsWith('https://')) {
        return tour.featured_image;
      }
      return `${apiUrl}/storage/${tour.featured_image}`;
    }
    
    return getFallbackImage(tour.categories);
  };

  // Get category name
  const getCategoryName = (tour) => {
    if (Array.isArray(tour.categories) && tour.categories.length > 0) {
      return tour.categories[0].name;
    }
    return tour.category || 'Tour';
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 featured-tours-grid">
      {tours.map((tour) => (
        <div key={tour.id} className="tour-card group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
          {/* Tour Image */}
          <div className="relative overflow-hidden h-56 bg-gray-100">
            <img 
              src={getImageUrl(tour)} 
              alt={tour.name || tour.title} 
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-in-out"
              onError={() => handleImageError(tour.id)}
              loading="eager"
            />
            {/* Category Tag */}
            <div className="absolute top-4 left-4 bg-primary/90 backdrop-blur-sm text-white text-xs uppercase tracking-wider py-1.5 px-3 rounded-full font-medium shadow-md">
              {getCategoryName(tour)}
            </div>
            
            {/* Price Badge */}
            <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm text-primary text-sm font-bold py-1.5 px-3 rounded-full shadow-lg">
              ${formatPrice(tour.price)}
            </div>
          </div>
          
          {/* Tour Info */}
          <div className="p-5 flex flex-col">
            {/* Title and Rating */}
            <div className="mb-3">
              <h3 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors">
                {tour.name || tour.title}
              </h3>
              <div className="flex items-center">
                <div className="flex items-center text-yellow-500 mr-2">
                  {[...Array(Math.floor(tour.rating || 4.5))].map((_, i) => (
                    <StarIcon key={i} className="h-4 w-4" />
                  ))}
                  {(tour.rating || 4.5) % 1 > 0 && (
                    <svg className="h-4 w-4 text-yellow-500" viewBox="0 0 24 24" fill="currentColor">
                      <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <span className="text-xs text-gray-500">({tour.reviews_count || 0} reviews)</span>
              </div>
            </div>
            
            {/* Features */}
            <div className="space-y-2 mb-4">
              {/* Location */}
              <div className="flex items-center text-gray-600">
                <MapPinIcon className="h-4 w-4 text-primary mr-2 flex-shrink-0" />
                <span className="text-sm truncate">{tour.location}</span>
              </div>
              
              {/* Duration */}
              <div className="flex items-center text-gray-600">
                <ClockIcon className="h-4 w-4 text-primary mr-2 flex-shrink-0" />
                <span className="text-sm">{tour.duration} {tour.duration === 1 ? 'day' : 'days'}</span>
              </div>
            </div>
            
            {/* View Details Button */}
            <div className="mt-auto pt-2">
              <Link 
                to={`/tours/${tour.slug}`}
                className="block w-full bg-primary hover:bg-primary-dark text-white text-center font-medium py-2.5 px-4 rounded-lg transition-colors shadow-sm hover:shadow-md"
              >
                View Details
              </Link>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FeaturedTours;
